/**
 * Research Data Viewer Component
 * Displays research service data with filtering and actions
 */

import React from 'react';
import type { ResearchDataItem } from '../../types/research-service';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ExternalLink, RefreshCw } from 'lucide-react';

interface ResearchDataViewerProps {
  data: ResearchDataItem[];
  title: string;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  showEmbeddings?: boolean;
}

export function ResearchDataViewer({
  data,
  title,
  loading = false,
  error = null,
  onRefresh,
  showEmbeddings = false,
}: ResearchDataViewerProps) {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-700">{title} - Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error.message}</p>
          {onRefresh && (
            <Button
              onClick={onRefresh}
              variant="outline"
              size="sm"
              className="mt-4"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-2">
            {data.length > 0 && (
              <Badge variant="secondary">{data.length} results</Badge>
            )}
            {onRefresh && (
              <Button
                onClick={onRefresh}
                variant="ghost"
                size="sm"
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No data available
          </div>
        ) : (
          <div className="space-y-4">
            {data.map((item) => (
              <ResearchDataItem
                key={item.id}
                item={item}
                showEmbedding={showEmbeddings}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Individual Data Item Component
// ============================================================================

interface ResearchDataItemProps {
  item: ResearchDataItem;
  showEmbedding?: boolean;
}

function ResearchDataItem({ item, showEmbedding = false }: ResearchDataItemProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      {/* Main Text */}
      <p className="mb-3 text-sm leading-relaxed text-gray-900">{item.text}</p>

      {/* Metadata */}
      <div className="space-y-2">
        {/* Keywords */}
        {item.metadata.keywords && item.metadata.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.metadata.keywords.map((keyword, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {keyword}
              </Badge>
            ))}
          </div>
        )}

        {/* Sentiment/Stance */}
        <div className="flex flex-wrap gap-2 text-xs">
          {item.metadata.sentiment && (
            <span className="rounded bg-blue-100 px-2 py-1 text-blue-700">
              Sentiment: {item.metadata.sentiment}
            </span>
          )}
          {item.metadata.stance && (
            <span className="rounded bg-purple-100 px-2 py-1 text-purple-700">
              Stance: {item.metadata.stance}
            </span>
          )}
          {item.score !== null && (
            <span className="rounded bg-green-100 px-2 py-1 text-green-700">
              Score: {item.score}
            </span>
          )}
        </div>

        {/* Source Information */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              Source
            </a>
          )}
          {item.source_subreddit && (
            <span>r/{item.source_subreddit}</span>
          )}
          {item.source_timestamp && (
            <span>
              {new Date(item.source_timestamp).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Embedding (optional) */}
        {showEmbedding && item.embedding && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500">
              Show embedding ({item.embedding.length}d vector)
            </summary>
            <pre className="mt-2 max-h-32 overflow-auto rounded bg-gray-50 p-2 text-xs">
              {JSON.stringify(item.embedding.slice(0, 10), null, 2)}...
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default ResearchDataViewer;
